import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import { PRO_EMAIL_MONTHLY_LIMIT } from "contexts/Subscription";
import { Plans } from "schemas/payments";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";

const checklistItems = {
  pro: [
    `Send ${PRO_EMAIL_MONTHLY_LIMIT} emails per month`,
    'Advanced Brand Filters (e.g. "Brands that work with UGC creators")',
    "Access to 600k+ Brand Contacts",
    "Follow Up Assistant with automated reminders",
  ],
  ai: [
    "300 automated emails on your behalf + 700 self serve",
    "Exclusive Filters and Brand Pay Data",
    "Access to 600k+ Brand Contacts",
    "Follow Up Assistant with automated reminders",
  ],
};

interface ChecklistProps {
  items: string[];
  iconColor: string;
}

const Checklist = ({ items, iconColor }: ChecklistProps) => (
  <Stack spacing={1.5}>
    {items.map((item) => (
      <Box
        key={item}
        sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            backgroundColor: iconColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: "2px",
          }}
        >
          <Box
            component="i"
            className="fa-regular fa-circle-check"
            sx={{ color: "white" }}
          />
        </Box>
        <Typography sx={{ color: "white", lineHeight: 1.55, fontSize: 16 }}>
          {item}
        </Typography>
      </Box>
    ))}
  </Stack>
);

interface PriceProps {
  amount: string;
}

const Price = ({ amount }: PriceProps) => (
  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5 }}>
    <Typography
      sx={{ fontSize: 48, fontWeight: 800, color: "#fff", lineHeight: 1 }}
    >
      {amount}
    </Typography>
    <Typography
      sx={{ fontSize: 16, color: "rgba(255,255,255,0.6)", mb: "6px" }}
    >
      / year
    </Typography>
  </Box>
);

interface Props {
  checkOutForSplashPage: (plan: Plans) => void;
}

const AnnualPlans = ({ checkOutForSplashPage }: Props) => {
  return (
    <Box
      sx={{
        px: { xs: 3, md: 6 },
        py: { xs: 6, md: 8 },
        backgroundColor: "#fff",
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: "#101828", mb: 1.5 }}
        >
          Choose Your Plan
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "#667085", maxWidth: 500, mx: "auto" }}
        >
          Save 25% with annual billing. Both plans include access to our full
          platform.
        </Typography>
      </Box>

      {/* Cards */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          maxWidth: 860,
          mx: "auto",
        }}
      >
        {/* Pro Card */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "#2D4A3E",
            borderRadius: "16px",
            p: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box>
            <Chip
              label="Great for creators who pitch often"
              size="small"
              sx={{
                backgroundColor: "#E8F36D",
                color: "#101828",
                px: 0.5,
                fontSize: "0.75rem",
                mb: 2,
              }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#fff", mb: 1.5 }}
            >
              Pro Plan
            </Typography>
            <Price amount="$300" />
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

          <Checklist items={checklistItems.pro} iconColor="#5A8A6A" />

          <Box sx={{ mt: "auto", pt: 1 }}>
            <StyledLargeButton
              fullWidth
              color="secondary"
              styles={{
                backgroundColor: "#fff",
                color: "#2D4A3E",

                "&:hover": { backgroundColor: "#f0f0f0" },
              }}
              onClick={() => checkOutForSplashPage(Plans.PRO_YEARLY)}
            >
              Get Started with Pro
            </StyledLargeButton>
          </Box>
        </Box>

        {/* AI Virtual Assistant Card */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "#1A1A1A",
            borderRadius: "16px",
            p: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: "column",
            gap: 3,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Subtle purple glow accent */}
          <Box
            sx={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 200,
              height: 200,
              borderRadius: "50%",
              backgroundColor: "rgba(200, 182, 255, 0.1)",
              filter: "blur(40px)",
              pointerEvents: "none",
            }}
          />

          <Box>
            <Chip
              label="Hands-off pitching for busy creators"
              size="small"
              sx={{
                backgroundColor: "#C8B6FF",
                color: "#101828",
                px: 0.5,
                fontSize: "0.75rem",
                mb: 2,
              }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#fff", mb: 1.5 }}
            >
              AI Virtual Assistant
            </Typography>
            <Price amount="$650" />
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

          <Checklist items={checklistItems.ai} iconColor="#7B5EA7" />

          <Box sx={{ mt: "auto", pt: 1 }}>
            <StyledLargeButton
              fullWidth
              color="secondary"
              styles={{
                backgroundColor: "#fff",
                color: "#1A1A1A",

                "&:hover": { backgroundColor: "#f0f0f0" },
              }}
              onClick={() => checkOutForSplashPage(Plans.VA_YEARLY)}
            >
              Get Started with AI Assistant
            </StyledLargeButton>
          </Box>
        </Box>
      </Box>

      <Typography
        textAlign={"center"}
        sx={{ color: "black", mt: 3, fontSize: 16 }}
      >
        🔒 Secure checkout powered by Stripe
      </Typography>
      <Typography textAlign={"center"} sx={{ color: "#667085", mt: 1 }}>
        Cancel anytime. No questions asked.
      </Typography>
    </Box>
  );
};

export default AnnualPlans;
